// AI Upgraded Service
import { Injectable } from '@angular/core';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { ComplianceService } from '@client/app/blocks/service/compliance/compliance.service';
import { ActiveFeatureService } from '@client/ng2/services/active-feature/active-feature.service';
import { HistoryHelperService } from '@client/app/blocks/helpers/history.helper.service';
import { MonNumberPipe } from '@monsido/ng2/core/locale/pipes/mon-number.pipe';
import { DatePipe } from '@angular/common';

@Injectable({
    providedIn: 'root',
})
export class ExporterSeoParserService {
    constructor (
        private readonly translateService: TranslateService,
        private readonly complianceService: ComplianceService,
        private readonly activeFeatureService: ActiveFeatureService,
        private readonly historyHelperService: HistoryHelperService,
        private readonly monNumberPipe: MonNumberPipe,
        private readonly datePipe: DatePipe,
    ) {}

    parse (data: any[]): any[] {
        if (!data?.length) {
            return [];
        }

        const csv: string[][] = [];
        const allowVerticalAverage = this.activeFeatureService.isFeatureActive('monsido_score');

        csv.push(this.getHeadlines(allowVerticalAverage));

        data.forEach((item) => {
            const entry: any[] = [];
            entry.push(this.datePipe.transform(item.post_processing_done_at ?? item.date, 'MMM d, y, h:mm a'));
            entry.push(item.page_count);

            if (item.compliancePercentage != null) {
                entry.push(this.monNumberPipe.transform(item.compliancePercentage / 100, '0.[00]%'));
            } else {
                entry.push(this.getCompliance(item));
            }

            if (allowVerticalAverage) {
                entry.push(item.comparisonToAverage);
            }

            if (item.opportunities) {
                entry.push(item.opportunities.seo_errors_alerts_count);
                entry.push(item.opportunities.seo_errors_warnings_count);
                entry.push(item.opportunities.seo_errors_infos_count);
                entry.push(item.opportunities.seo_errors_technical_count);
            } else {
                entry.push(item.seo_errors_alerts_count);
                entry.push(item.seo_errors_warnings_count);
                entry.push(item.seo_errors_infos_count);
                entry.push(item.seo_errors_technical_count);
            }

            entry.push(this.historyHelperService.getCaretCharacter(item.changeValueIconClass) + ' ' + item.changePercentageValue);

            csv.push(entry);
        });

        return csv;
    }

    private getHeadlines (allowVerticalAverage: boolean): string[] {
        const headlines = [
            this.translateService.getString('Crawled at'),
            this.translateService.getString('Pages crawled'),
            this.translateService.getString('Domain compliance'),
        ];

        if (allowVerticalAverage) {
            headlines.push(this.translateService.getString('Comparison to average'));
        }

        headlines.push(
            this.translateService.getString('SEO opportunities - high priority'),
            this.translateService.getString('SEO opportunities - medium priority'),
            this.translateService.getString('SEO opportunities - low priority'),
            this.translateService.getString('SEO opportunities - technical SEO issues'),
            this.translateService.getString('Changes since previous crawl'),
        );

        return headlines;
    }

    private getCompliance (item: any): string {
        item.seoComplianceData ??= this.complianceService.seoHistoryCompliance(item);
        return this.monNumberPipe.transform(item.seoComplianceData?.[0] / 100, '0.[00]%') || '';
    }

    private getCaret (id: any, data: any[], issuesStack: string[]): string {
        const value = this.historyHelperService.getChangeValue(id, data, issuesStack);
        if (value > 0) {
            return String.fromCharCode(94);
        } else if (value < 0) {
            return 'ˇ';
        }
        return '';
    }
}
